import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Building2, KeyRound, Power, PowerOff, Store, Users } from 'lucide-react'
import { toast } from 'sonner'
import {
  activateEmpresaAdmin,
  deactivateEmpresaAdmin,
  fetchAdminEmpresaDetail,
  sendSupportPasswordReset,
} from '../../api'
import { RANK_BADGE_VARIANT, RANK_LABELS } from '../../lib/rank'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'

export function EmpresaDetailPage() {
  const { empresaId } = useParams<{ empresaId: string }>()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['admin', 'empresas', empresaId],
    queryFn: () => fetchAdminEmpresaDetail(empresaId!),
    enabled: !!empresaId,
  })

  const resetMutation = useMutation({
    mutationFn: sendSupportPasswordReset,
    onSuccess: (_, email) => toast.success(`Enviamos un enlace de reset de contraseña a ${email}.`),
    onError: () => toast.error('No se pudo enviar el reset de contraseña.'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: () => (query.data?.is_active ? deactivateEmpresaAdmin(empresaId!) : activateEmpresaAdmin(empresaId!)),
    onSuccess: (empresa) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'empresas', empresaId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'empresas'] })
      toast.success(empresa.is_active ? 'Empresa reactivada.' : 'Empresa desactivada — sus usuarios ya no pueden loguear y su bot dejó de responder.')
    },
    onError: () => toast.error('No se pudo cambiar el estado de la Empresa.'),
  })

  return (
    <div className="flex flex-col gap-6">
      <Link to="/admin/empresas" className="flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" />
        Volver a Empresas
      </Link>

      {query.isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
      {query.isError && <p className="text-sm text-destructive">No se pudo cargar esta Empresa.</p>}

      {query.data && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
                <Building2 className="size-5 text-muted-foreground" />
                {query.data.nombre}
                <Badge variant={query.data.is_active ? 'outline' : 'destructive'}>
                  {query.data.is_active ? 'Activa' : 'Desactivada'}
                </Badge>
              </h1>
              <p className="text-sm text-muted-foreground">
                {query.data.email_contacto ?? 'Sin email de contacto'} · alta{' '}
                {new Date(query.data.created_at).toLocaleDateString('es-CR')}
              </p>
            </div>
            <Button
              variant={query.data.is_active ? 'destructive' : 'default'}
              disabled={toggleActiveMutation.isPending}
              onClick={() => toggleActiveMutation.mutate()}
            >
              {query.data.is_active ? <PowerOff className="size-4" /> : <Power className="size-4" />}
              {query.data.is_active ? 'Desactivar' : 'Activar'}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Store className="size-4" />
                Sociedades ({query.data.sociedades.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {query.data.sociedades.length === 0 && <p className="text-sm text-muted-foreground">Sin Sociedades.</p>}
              {query.data.sociedades.map((s) => (
                <Badge key={s.id} variant="outline">
                  {s.nombre}
                </Badge>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="size-4" />
                Miembros ({query.data.miembros.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col divide-y">
              {query.data.miembros.length === 0 && <p className="text-sm text-muted-foreground">Sin miembros.</p>}
              {query.data.miembros.map((m) => (
                <div key={`${m.user_id}-${m.sociedad_nombre}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{m.nombre}</p>
                    <p className="text-muted-foreground">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>{m.sociedad_nombre}</span>
                    <Badge variant={RANK_BADGE_VARIANT[m.rank]}>{RANK_LABELS[m.rank]}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={resetMutation.isPending}
                      onClick={() => resetMutation.mutate(m.email)}
                    >
                      <KeyRound className="size-3.5" />
                      Enviar reset de contraseña
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
